import { caseStudies, caseStudyPath } from '../data/CaseStudies';
import { insightPath, insights } from '../data/Insights';

// Only fixed, public paths may be included in a replay. In particular, a
// pathname is not safe merely because it belongs to this site's origin.
const replayPaths = new Set([
  '/',
  '/about-us',
  '/solutions',
  '/services',
  '/case-studies',
  ...caseStudies.map((study) => caseStudyPath(study.id)),
  '/cms-governance',
  '/insights',
  ...insights.map((article) => insightPath(article.id)),
  '/faq',
  '/privacy',
]);

/** Return a canonical public URL without visitor-controlled query or fragment. */
export function replayPublicUrl(value: unknown, origin: string): string | null {
  if (typeof value !== 'string' || !value) return null;
  try {
    const base = new URL(origin);
    if (
      base.protocol !== 'https:' ||
      !['scaledtech.consulting', 'www.scaledtech.consulting'].includes(base.hostname) ||
      base.port ||
      base.username ||
      base.password ||
      base.pathname !== '/' ||
      base.search ||
      base.hash
    )
      return null;
    const url = new URL(value, base);
    if (
      url.origin !== base.origin ||
      url.username ||
      url.password ||
      !replayPaths.has(url.pathname)
    )
      return null;
    return `${base.origin}${url.pathname}`;
  } catch {
    return null;
  }
}

const replayMetadataKeys = new Set([
  'type',
  'replay_start_timestamp',
  'timestamp',
  'error_ids',
  'trace_ids',
  'segment_names',
  'urls',
  'replay_id',
  'segment_id',
  'replay_type',
  'event_id',
  'environment',
  'sdk',
  'contexts',
  'transaction',
  'platform',
]);
const identifier = /^[a-f0-9]{32}$/;

/** Rebuild replay metadata from known fields; never forward contextual data. */
export function replayMetadata(value: unknown, origin: string): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const event = value as Record<string, unknown>;
  if (Object.keys(event).some((key) => !replayMetadataKeys.has(key))) return null;
  if (
    event.type !== 'replay_event' ||
    event.environment !== 'production' ||
    event.platform !== 'javascript' ||
    (event.replay_type !== 'session' && event.replay_type !== 'buffer') ||
    typeof event.event_id !== 'string' ||
    !identifier.test(event.event_id) ||
    typeof event.replay_id !== 'string' ||
    !identifier.test(event.replay_id) ||
    !Number.isSafeInteger(event.segment_id) ||
    (event.segment_id as number) < 0 ||
    typeof event.timestamp !== 'number' ||
    !Number.isFinite(event.timestamp) ||
    typeof event.replay_start_timestamp !== 'number' ||
    !Number.isFinite(event.replay_start_timestamp)
  )
    return null;
  const validIds = (ids: unknown): ids is string[] =>
    Array.isArray(ids) && ids.every((id) => typeof id === 'string' && identifier.test(id));
  if (!validIds(event.error_ids) || !validIds(event.trace_ids)) return null;
  const safeUrls = (values: unknown): string[] | null => {
    if (!Array.isArray(values)) return null;
    const urls = values.map((url) => replayPublicUrl(url, origin));
    return urls.every((url): url is string => url !== null) ? urls : null;
  };
  const urls = safeUrls(event.urls);
  const names =
    Array.isArray(event.segment_names) &&
    event.segment_names.every((name) =>
      ['Pageload', 'Navigation', 'Browser performance'].includes(name)
    )
      ? event.segment_names
      : null;
  const transaction =
    event.transaction === undefined ? undefined : replayPublicUrl(event.transaction, origin);
  if (!urls || !names || transaction === null) return null;
  return {
    type: 'replay_event',
    replay_start_timestamp: event.replay_start_timestamp,
    timestamp: event.timestamp,
    error_ids: event.error_ids,
    trace_ids: event.trace_ids,
    segment_names: names,
    urls,
    replay_id: event.replay_id,
    segment_id: event.segment_id,
    replay_type: event.replay_type,
    event_id: event.event_id,
    environment: 'production',
    ...(transaction ? { transaction } : {}),
    sdk: { name: 'sentry.javascript.react', version: '11.9.0', settings: { infer_ip: 'never' } },
    platform: 'javascript',
  };
}
